import { GlobalStyles } from '@experiences/theme';
import { UiText } from '@experiences/ui-common';
import { getEnvVariableValue } from '@experiences/util';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import type { FC } from 'react';
import React from 'react';
import {
    Controller,
    useFormContext,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import { GroupsAndRolesLink } from '../../../common/constants/documentation/DocumentationLinks.default';
import { useDocumentationLinks } from '../../../common/hooks/useDocumentationLink';
import type { IGroup } from '../../../common/interfaces/cis/group';
import type { IEditGroupMembershipData } from '../interfaces/user';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        groups: {
            marginTop: '10px',
            display: 'flex',
        },
        groupText: {
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        groupInfoIcon: {
            width: 18,
            height: 18,
            marginLeft: '4px',
            marginTop: '1px',
            cursor: 'pointer',
            color: theme.palette.semantic.colorPrimary,
        },
        inputLabel: {
            fontWeight: 600,
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        tooltipIcon: {
            width: 16,
            height: 16,
            marginLeft: '4px',
        },
    }),
}));

export const defaultEditGroupMembershipData: IEditGroupMembershipData = { groupIds: {} };

const EditGroupMembershipFormComponent: FC<{ groups: IGroup[] }> = ({ groups }) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { control } = useFormContext<IEditGroupMembershipData>();
    const getLocalizedLink = useDocumentationLinks();

    return (
        <div
            role="group"
            aria-labelledby="groupMembershipCheckboxListLabel"
            data-cy="group-membership-form-component">
            <div className={classes.groups}>
                <UiText
                    className={classes.inputLabel}
                    id="groupMembershipCheckboxListLabel">
                    {translate({ id: 'CLIENT_GROUP_MEMBERSHIP' })}
                </UiText>
                <Tooltip
                    arrow
                    title={
                        <div style={{ display: 'flex' }}>
                            {translate({ id: 'CLIENT_GROUPS_GUIDE' }, { titleProductName: getEnvVariableValue('TITLE_PRODUCT_NAME') })}
                            <OpenInNewIcon className={classes.tooltipIcon} />
                        </div>
                    }
                >
                    <a
                        className={classes.a}
                        href={getLocalizedLink({ articleSlug: GroupsAndRolesLink })}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={translate({ id: 'CLIENT_GROUPS_GUIDE_LINK_ARIA' })}
                    >
                        <HelpOutlineIcon className={classes.groupInfoIcon} />
                    </a>
                </Tooltip>
            </div>
            <FormGroup>
                {groups.map((group, i) => (
                    <Controller
                        key={i}
                        name={`groupIds.${group.id}`}
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name={`groupIds.${group.id}`}
                                        size="small"
                                        checked={!!field.value}
                                        onChange={e => field.onChange(e.target.checked)}
                                    />
                                }
                                label={
                                    <UiText className={classes.groupText}>
                                        {group.name}
                                    </UiText>
                                }
                                disabled={group.name === 'Everyone'}
                            />
                        )}
                    />
                ))}
            </FormGroup>
        </div>
    );
};

export default EditGroupMembershipFormComponent;
